/* eslint-disable react/forbid-prop-types,react-hooks/exhaustive-deps */
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import LocalizedText, { localize } from 'commons/LocalizedText/LocalizedText';
import SkeletonTable from 'components/Skeletons/SkeletonTable/SkeletonTable';
import useMobile from 'hook/UseMobile';
import useQueryState from 'hook/UseQueryState';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ParamTypes } from 'utils/constants/param.type';
import { MOBILE_BREAKPOINT } from 'utils/constants/responsive';

const MIN_ROWS_FOR_PAGINATION = 5;
export const INITIAL_PAGE = 0;
export const INITIAL_ROWS_PER_PAGE = 10;

const CELL_STYLE = { maxWidth: '30vw', overflowX: 'overlay', overflowY: 'hidden', wordWrap: 'break-word' };

const useStyles = makeStyles((theme) => ({
  table: {
    tableLayout: 'fixed'
  },
  white: {
    backgroundColor: theme.palette.background.paper
  },
  rowStriped: {
    '&:nth-child(even)': {
      backgroundColor: theme.palette.primary.light
    },
    '& > td': {
      [theme.breakpoints.down('sm')]: {
        padding: '2px 10px 2px 10px'
      }
    }
  },
  hoverRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.tableHover
    }
  },
  tableHeader: {
    ...CELL_STYLE,
    borderBottom: '1px solid black',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
      padding: '3px 12px 3px 12px'
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '1px 8px 1px 8px'
    }
  },
  tableCell: {
    ...CELL_STYLE
  },
  overflowHidden: {
    overflow: 'hidden'
  }
}));

const desc = (a, b, orderBy, sortOnProperty) => {
  if (b[orderBy] && b[orderBy][sortOnProperty]) {
    if (b[orderBy][sortOnProperty] < a[orderBy][sortOnProperty]) return -1;
    if (b[orderBy][sortOnProperty] > a[orderBy][sortOnProperty]) return 1;
  }else{
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
  }
  return 0;
};

export const CustomTable = ({
  rows, headers, onRowCLick, stickyHeader, filters, isLoading, rowKey, onChange, defaultOrder,
  isBackend, total, needForFooter
}) => {
  const classes = useStyles();
  const isMobile = useMobile();
  const [order, setOrder] = useQueryState('order', defaultOrder || 'asc', ParamTypes.STRING);
  const [orderBy, setOrderBy] = useQueryState('orderBy', headers.orderBy || '', ParamTypes.STRING);
  const [sortOnProperty, setSortOnProperty] = useState(headers.sortOn || 'name');
  const [filteredRows, setFilteredRows] = useState(rows);
  const [page, setPage] = useQueryState('page', INITIAL_PAGE, ParamTypes.NUMBER);
  const [rowsPerPage, setRowsPerPage] = useQueryState('rowsPerPage', INITIAL_ROWS_PER_PAGE, ParamTypes.NUMBER);

  // FRONTEND: Handle filters for frontend grid (reset to first page every time filters change)
  useEffect(() => {
    if (!isBackend) {
      setPage(0);
      let newRows = [...rows];
      Object.values(filters)
        .forEach((filter) => {
          newRows = newRows.filter(filter);
        });
      setFilteredRows(newRows);
    }
  }, [filters, rows]);

  // BACKEND/FRONTEND : Handle filters for backend grid
  useEffect(() => {
    if (isBackend) {
      setFilteredRows([...rows]);
    }
  }, [rows]);

  // BACKEND: Reset to first page when new backend filter is activated (number of pages change) + refresh grid
  useEffect(() => {
    setPage(0);
    onChange();
  }, [total]);

  const handleSort = ({ name, sortOn }) => {
    const isDesc = orderBy === name && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(name);
    setSortOnProperty(sortOn);
    onChange();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    onChange();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    onChange();
  };


  const getSorting = () => (order === 'desc'
    ? (a, b) => desc(a, b, orderBy, sortOnProperty)
    : (a, b) => -desc(a, b, orderBy, sortOnProperty));

  const finalRows = isBackend
    ? rows
    : filteredRows.sort(getSorting()).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const finalHeaders = isMobile
    ? headers.filter((header) => header.isOnMobile)
    : headers;

  const needForPages = isBackend || rows.length > MIN_ROWS_FOR_PAGINATION;

  return (
    <>
      <Table
        className={[classes.white, classes.table].join(' ')}
        size="small"
        stickyHeader={stickyHeader}
      >
        <TableHead>
          <TableRow>
            {finalHeaders.map((header) => (
              <TableCell
                className={`${classes.white} ${classes.tableHeader}`}
                key={header.name}
                width={isMobile ? header.mobileWidth : header.width}
              >
                {header.isSortable ? (
                  <TableSortLabel
                    active={orderBy === header.name}
                    direction={order}
                    onClick={() => handleSort({ ...header })}
                  >
                    <LocalizedText text={header.label} />
                  </TableSortLabel>
                )
                  : <LocalizedText text={header.label} />}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {isLoading
          ? <SkeletonTable columns={finalHeaders.length} rows={rowsPerPage} />
          : (
            <TableBody>
              {finalRows.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={finalHeaders.length}>
                      <LocalizedText align="center" variant="h6">noDataInTable</LocalizedText>
                    </TableCell>
                  </TableRow>
                )
                : finalRows.map((row) => (
                  <TableRow
                    className={`${classes.rowStriped} ${onRowCLick ? classes.hoverRow : ''}`}
                    key={row.id}
                    onClick={() => onRowCLick && onRowCLick(row)}
                  >
                    {finalHeaders.map((column) => (
                      <TableCell
                        className={classes.tableCell}
                        key={column.name}
                        width={isMobile ? column.mobileWidth : column.width}
                      >
                        {column.template(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          )}
      </Table>
      {(needForPages && needForFooter) && (
        <TableFooter>
          <TableRow>
            <TablePagination
              backIconButtonText={localize('previousPage')}
              className={classes.overflowHidden}
              count={total ?? filteredRows.length}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to === -1 ? count : to} ${localize('to')} ${count}`}
              labelRowsPerPage={localize('rowsPerPage')}
              nextIconButtonText={localize('nextPage')}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </TableRow>
        </TableFooter>
      )}
    </>
  );
};

CustomTable.propTypes = {
  filters: PropTypes.object,
  headers: PropTypes.arrayOf(PropTypes.shape({
    isOnMobile: PropTypes.bool,
    isSortable: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    sortOn: PropTypes.string,
    template: PropTypes.func
  })).isRequired,
  isBackend: PropTypes.bool,
  isLoading: PropTypes.bool,
  needForFooter: PropTypes.bool,
  onChange: PropTypes.func,
  onRowCLick: PropTypes.func,
  rowKey: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stickyHeader: PropTypes.bool,
  total: PropTypes.number
};

CustomTable.defaultProps = {
  filters: {},
  isBackend: false,
  isLoading: false,
  needForFooter: true,
  onChange: () => {},
  onRowCLick: null,
  rowKey: null,
  rows: [],
  stickyHeader: false,
  total: null
};